import { AfterRenderRef, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, Injector, OnDestroy, OnInit, ViewChild, afterNextRender } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, Validators } from '@angular/forms';
import { BehaviorSubject, Subject, combineLatest, interval, of, timer, Observable } from 'rxjs';
import { map, catchError, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ScreenSaverService } from '@app/core/services/screen-saver.service';
import { BadisOauthLoginService } from '@app/core/services/auth/badis-oauth-login.service';
import { TwoFactorInputComponent } from '@app/shared/components/two-factor-input/two-factor-input.component';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '@app/core/services/data.service';
import { UserAccessToken } from '@app/core/services/auth/model/auth-user.interface';
import { ImageService } from '@app/shared/services/image.service';
import { LandingMediaManifest, LandingMediaService, LandingSolutionMediaKey } from '@app/core/services/landing-media.service';

interface ScreensaverManifest {
    images: string[];
}

interface ScreensaverTipTranslation {
    Title?: string;
    Desc?: string;
}

interface ScreensaverTip {
    mediaKey: LandingSolutionMediaKey;
    title: string;
    description: string;
    videoUrl?: string;
    coverUrl?: string;
}

interface ScreensaverTipViewModel {
    tip: ScreensaverTip | null;
    displayIndex: number;
    total: number;
    shouldShowCover: boolean;
}

interface ScreensaverUiViewModel {
    unlockVisible: boolean;
    showUserAvatar: boolean;
    showUserIdentity: boolean;
}

interface TipVideoLoadContext {
    video?: HTMLVideoElement;
    expectedUrl: string | null;
    currentUrl: string | null;
}

@Component({
    selector: 'app-screensaver',
    templateUrl: './screensaver.component.html',
    styleUrls: ['./screensaver.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ScreensaverComponent implements OnInit, OnDestroy {
    @ViewChild('unlockInput') unlockInput?: TwoFactorInputComponent;
    @ViewChild('currentPinInput') currentPinInput?: TwoFactorInputComponent;
    @ViewChild('newPinInput') newPinInput?: TwoFactorInputComponent;
    @ViewChild('tipVideo') set tipVideoRef(value: ElementRef<HTMLVideoElement> | undefined) {
        this.tipVideo = value;
        this.scheduleForceTipVideoLoad();
    }

    private readonly fallbackImages = [
        'assets/screensaver/space-1.jpg',
        'assets/screensaver/space-2.jpg',
        'assets/screensaver/space-3.jpg'
    ];

    private readonly imagesSubject = new BehaviorSubject<string[]>(this.fallbackImages);
    readonly now$ = timer(0, 1000).pipe(map(() => new Date()));
    readonly images$: Observable<string[]> = this.imagesSubject.asObservable();
    private readonly activeIndexSubject = new BehaviorSubject<number>(0);
    readonly activeIndex$: Observable<number> = this.activeIndexSubject.asObservable();
    sessionRemainingLabel$: Observable<string> = of('');
    private activeIndex = 0;
    private imageCount = this.fallbackImages.length;
    private readonly defaultTips: ScreensaverTip[] = [
        {
            mediaKey: 'live-asset-tracking',
            title: 'LIVE ASSET TRACKING',
            description: 'Monitor zones, movement, dwell time, and handover bottlenecks in real time.'
        },
        {
            mediaKey: 'cold-chain-monitoring',
            title: 'COLD CHAIN MONITORING',
            description: 'Track temperature-sensitive assets and react before compliance thresholds are missed.'
        },
        {
            mediaKey: 'people-and-safety',
            title: 'PEOPLE & SAFETY',
            description: 'Keep teams visible during evacuations, panic alerts, and controlled workforce flows.'
        },
        {
            mediaKey: 'insight-suite',
            title: 'INSIGHT SUITE',
            description: 'Turn live RTLS signals into heatmaps, occupancy trends, and decision-ready analytics.'
        },
        {
            mediaKey: 'predictive-maintenance',
            title: 'PREDICTIVE MAINTENANCE',
            description: 'Correlate movement, anomalies, and machine context to surface early maintenance risk.'
        },
        {
            mediaKey: 'yard-management',
            title: 'YARD MANAGEMENT',
            description: 'Coordinate trailers, loading lanes, and gate flow from one live operational view.'
        },
        {
            mediaKey: 'compliance-and-audit',
            title: 'COMPLIANCE & AUDIT',
            description: 'Document patrols, checkpoints, and traceable workflow evidence across the operation.'
        }
    ];
    private readonly tipViewModelSubject = new BehaviorSubject<ScreensaverTipViewModel>({
        tip: this.defaultTips[0] ?? null,
        displayIndex: this.defaultTips.length ? 1 : 0,
        total: this.defaultTips.length,
        shouldShowCover: false
    });
    readonly tipViewModel$ = this.tipViewModelSubject.asObservable();
    private readonly uiViewModelSubject = new BehaviorSubject<ScreensaverUiViewModel>({
        unlockVisible: false,
        showUserAvatar: false,
        showUserIdentity: false
    });
    readonly uiViewModel$ = this.uiViewModelSubject.asObservable();
    tips: ScreensaverTip[] = [...this.defaultTips];
    activeTipIndex = 0;
    activeTip: ScreensaverTip | null = this.defaultTips[0] ?? null;
    activeTipDisplayIndex = 1;
    shouldShowTipCover = false;
    showUserAvatar = false;
    showUserIdentity = false;
    tipVideoReady = false;
    tipVideoLoadFailed = false;
    unlockVisible = false;
    hasPin = false;
    overlayMode: 'unlock' | 'setup' = 'unlock';
    setupStep: 'current' | 'new' = 'new';
    pinErrorKey: string | null = null;
    lockoutRemaining = 0;
    showLogoutAction = false;
    displayName = '';
    avatarUrl: string | null = null;
    private avatarLoadFailed = false;
    pinControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{4}$/)]);
    currentPinControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{4}$/)]);
    newPinControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{4}$/)]);
    private destroy$ = new Subject<void>();
    private readonly rotationMs = 12000;
    private readonly tipVideoFallbackMs = 30000;
    private readonly tipStaticFallbackMs = 12000;
    private readonly panelIdleMs = 10000;
    private tipVideo?: ElementRef<HTMLVideoElement>;
    private requestedTipVideoUrl: string | null = null;
    private scheduledActiveIndexRef?: AfterRenderRef;
    private scheduledTipVideoLoadRef?: AfterRenderRef;
    private scheduledTipVideoStateRef?: AfterRenderRef;
    private scheduledUnlockRef?: AfterRenderRef;
    private scheduledDerivedStateRef?: AfterRenderRef;
    private exitArmedAt = 0;
    private hidePanelTimeout?: ReturnType<typeof setTimeout>;
    private focusTimeout?: ReturnType<typeof setTimeout>;
    private tipRotationTimeout?: ReturnType<typeof setTimeout>;
    private failedUnlockAttempts = 0;
    private lockoutUntil = 0;
    private lockoutTimer?: ReturnType<typeof setInterval>;

    constructor(
        private http: HttpClient,
        private screenSaver: ScreenSaverService,
        private authService: BadisOauthLoginService,
        private translate: TranslateService,
        private dataService: DataService,
        private imageService: ImageService,
        private landingMedia: LandingMediaService,
        private changeDetectorRef: ChangeDetectorRef,
        private injector: Injector,
        private destroyRef: DestroyRef
    ) { }

    ngOnInit(): void {
        this.exitArmedAt = Date.now() + 600;
        this.loadImages();
        this.loadTips();
        this.initializeOverlayMode();
        this.bindSessionCountdown();
        this.setUserIdentity(this.authService.userAccessToken);
        this.syncDerivedState();

        this.screenSaver.pinState$
            .pipe(takeUntil(this.destroy$))
            .subscribe((hasPin) => {
                this.hasPin = hasPin;
                this.syncDerivedState();
            });

        this.authService.onAuthStatusChanged
            .pipe(takeUntil(this.destroy$))
            .subscribe((isAuthenticated: boolean) => {
                if (isAuthenticated) {
                    this.setUserIdentity(this.authService.userAccessToken);
                    return;
                }
                this.setUserIdentity(null);
            });

        this.pinControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.pinErrorKey = null;
                this.showLogoutAction = false;
            });

        this.currentPinControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.pinErrorKey = null;
            });

        this.newPinControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.pinErrorKey = null;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearScheduledActiveIndexUpdate();
        this.clearScheduledTipVideoLoad();
        this.clearScheduledTipVideoStateUpdate();
        this.clearScheduledUnlock();
        this.clearScheduledDerivedStateSync();
        this.clearHidePanelTimer();
        this.clearFocusTimer();
        this.clearTipRotationTimer();
        this.clearLockoutTimer();
    }

    forceTipVideoMute(event: Event): void {
        const video = event.target as HTMLVideoElement | null;
        if (!video) {
            return;
        }
        video.muted = true;
        video.volume = 0;
    }

    onTipVideoLoadedData(event: Event): void {
        if (!this.isCurrentTipVideoEvent(event)) {
            return;
        }
        this.forceTipVideoMute(event);
        this.scheduleTipVideoStateUpdate(true, false);
    }

    onTipVideoCanPlay(event: Event): void {
        if (!this.isCurrentTipVideoEvent(event)) {
            return;
        }
        this.forceTipVideoMute(event);
        this.scheduleTipVideoStateUpdate(true, false);
    }

    onTipVideoPlay(event: Event): void {
        if (!this.isCurrentTipVideoEvent(event)) {
            return;
        }
        this.forceTipVideoMute(event);
        this.scheduleTipVideoStateUpdate(true, false);
    }

    onTipVideoEnded(): void {
        this.advanceTip();
    }

    onTipVideoError(): void {
        if (!this.isCurrentTipVideoElement()) {
            return;
        }
        this.scheduleTipVideoStateUpdate(false, true, () => this.scheduleTipRotation(this.tipStaticFallbackMs));
    }

    @HostListener('document:mousemove')
    @HostListener('document:keydown')
    onUserActivity(): void {
        if (Date.now() < this.exitArmedAt) {
            return;
        }
        this.scheduleShowUnlock();
    }

    @HostListener('document:click')
    @HostListener('document:touchstart')
    onUserClick(): void {
        if (Date.now() < this.exitArmedAt) {
            return;
        }

        if (this.overlayMode === 'unlock' && !this.screenSaver.hasPin()) {
            if (this.screenSaver.isPinRefreshPending()) {
                this.scheduleShowUnlock();
                return;
            }
            this.exit();
            return;
        }

        this.scheduleShowUnlock();
    }

    trackByIndex(index: number): number {
        return index;
    }

    private setActiveIndex(index: number): void {
        this.clearScheduledActiveIndexUpdate();
        if (this.destroyRef.destroyed) {
            return;
        }

        this.activeIndex = index;
        this.scheduledActiveIndexRef = afterNextRender(() => {
            this.scheduledActiveIndexRef = undefined;
            if (this.destroyRef.destroyed) {
                return;
            }

            this.activeIndexSubject.next(index);
        }, { injector: this.injector });
    }

    private clearScheduledActiveIndexUpdate(): void {
        if (!this.scheduledActiveIndexRef) {
            return;
        }

        this.scheduledActiveIndexRef.destroy();
        this.scheduledActiveIndexRef = undefined;
    }

    private syncDerivedState(): void {
        const tipCount = this.tips.length;
        const normalizedTipIndex = tipCount ? this.activeTipIndex % tipCount : 0;
        const activeTip = tipCount ? this.tips[normalizedTipIndex] : null;

        this.activeTip = activeTip;
        this.activeTipDisplayIndex = activeTip ? normalizedTipIndex + 1 : 0;
        this.shouldShowTipCover = !!activeTip?.coverUrl && (!activeTip.videoUrl || !this.tipVideoReady || this.tipVideoLoadFailed);
        this.showUserAvatar = this.unlockVisible && !!this.avatarUrl && !this.avatarLoadFailed;
        this.showUserIdentity = this.unlockVisible && this.overlayMode === 'unlock' && !!this.displayName;
        this.tipViewModelSubject.next({
            tip: activeTip,
            displayIndex: activeTip ? normalizedTipIndex + 1 : 0,
            total: tipCount,
            shouldShowCover: this.shouldShowTipCover
        });
        this.uiViewModelSubject.next({
            unlockVisible: this.unlockVisible,
            showUserAvatar: this.showUserAvatar,
            showUserIdentity: this.showUserIdentity
        });
    }

    private scheduleDerivedStateSync(): void {
        this.clearScheduledDerivedStateSync();
        if (this.destroyRef.destroyed) {
            return;
        }

        this.scheduledDerivedStateRef = afterNextRender(() => {
            this.scheduledDerivedStateRef = undefined;
            if (this.destroyRef.destroyed) {
                return;
            }

            this.syncDerivedState();
            this.changeDetectorRef.detectChanges();
        }, { injector: this.injector });
    }

    private clearScheduledDerivedStateSync(): void {
        if (!this.scheduledDerivedStateRef) {
            return;
        }

        this.scheduledDerivedStateRef.destroy();
        this.scheduledDerivedStateRef = undefined;
    }

    onAvatarError(): void {
        this.avatarLoadFailed = true;
        this.syncDerivedState();
    }

    private bindSessionCountdown(): void {
        this.sessionRemainingLabel$ = this.authService.sessionRemainingMs$.pipe(
            map((remainingMs) => {
                if (remainingMs <= 0) {
                    return '';
                }

                const totalSeconds = Math.floor(remainingMs / 1000);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            }),
            distinctUntilChanged()
        );
    }



    attemptUnlock(code?: string): void {
        if (!this.hasPin || this.overlayMode !== 'unlock') {
            return;
        }

        if (this.isLockedOut()) {
            this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.LOCKED';
            return;
        }

        const pin = (code ?? this.pinControl.value ?? '').toString();
        if (!/^\d{4}$/.test(pin)) {
            this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.INVALID';
            this.pinControl.setValue('', { emitEvent: false });
            this.unlockInput?.clear();
            this.scheduleFocus('unlock');
            return;
        }

        if (!this.screenSaver.verifyPin(pin)) {
            this.registerUnlockFailure();
            return;
        }

        this.resetLockoutState();
        this.screenSaver.deactivate();
    }

    startSetup(): void {
        this.overlayMode = 'setup';
        this.hasPin = this.screenSaver.hasPin();
        const skipCurrent = this.screenSaver.shouldSkipCurrentPin();
        this.setupStep = this.hasPin && !skipCurrent ? 'current' : 'new';
        this.resetSetupControls();
        this.unlockVisible = true;
        this.syncDerivedState();
        if (this.setupStep === 'current') {
            this.scheduleFocus('current');
        } else {
            this.scheduleFocus('new');
        }
    }

    exit(): void {
        this.screenSaver.deactivate();
    }

    proceedFromCurrent(code?: string): void {
        if (this.setupStep !== 'current') {
            return;
        }

        const pin = (code ?? this.currentPinControl.value ?? '').toString();
        if (!/^\d{4}$/.test(pin)) {
            this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.INVALID';
            this.currentPinControl.setValue('', { emitEvent: false });
            this.currentPinInput?.clear();
            this.scheduleFocus('current');
            return;
        }

        if (!this.screenSaver.verifyPin(pin)) {
            this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.INVALID_CURRENT';
            this.currentPinControl.setValue('', { emitEvent: false });
            this.currentPinInput?.clear();
            this.scheduleFocus('current');
            return;
        }

        this.setupStep = 'new';
        this.pinErrorKey = null;
        this.newPinControl.setValue('');
        this.syncDerivedState();
        this.scheduleFocus('new');
    }

    saveNewPin(): void {
        if (this.setupStep !== 'new') {
            return;
        }

        const newPin = (this.newPinControl.value ?? '').toString();
        if (!/^\d{4}$/.test(newPin)) {
            this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.INVALID';
            this.newPinControl.setValue('', { emitEvent: false });
            this.newPinInput?.clear();
            this.scheduleFocus('new');
            return;
        }

        this.screenSaver.setPin(newPin);
        this.exit();
    }

    cancelSetup(): void {
        this.pinErrorKey = null;
        this.resetSetupControls();
        if (this.screenSaver.shouldSkipCurrentPin()) {
            this.exit();
            return;
        }

        this.overlayMode = 'unlock';
        this.hasPin = this.screenSaver.hasPin();
        this.setupStep = this.hasPin ? 'current' : 'new';
        this.unlockVisible = true;
        this.syncDerivedState();
        this.scheduleHidePanel();
        if (this.hasPin) {
            this.scheduleFocus('unlock');
        }
    }

    private loadImages(): void {
        this.http.get<ScreensaverManifest>('assets/screensaver/manifest.json')
            .pipe(
                catchError(() => of({ images: this.fallbackImages })),
                map((data) => data?.images?.length ? data.images : this.fallbackImages),
                takeUntil(this.destroy$)
            )
            .subscribe((images) => {
                this.imageCount = Math.max(1, images.length);
                this.setActiveIndex(this.activeIndex % this.imageCount);
                this.imagesSubject.next(images);
            });
        this.startRotation();
    }

    private startRotation(): void {
        interval(this.rotationMs)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.setActiveIndex((this.activeIndex + 1) % Math.max(1, this.imageCount));
            });
    }

    private loadTips(): void {
        combineLatest([
            this.translate.stream('Screensaver.Tips').pipe(
                map((val) => Array.isArray(val) ? val.filter(Boolean) as ScreensaverTipTranslation[] : []),
                catchError(() => of([] as ScreensaverTipTranslation[]))
            ),
            this.landingMedia.getManifest()
        ])
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(([translations, manifest]) => {
                this.tips = this.defaultTips.map((tip, index) => this.resolveTip(tip, translations[index], manifest));
                this.activeTipIndex = 0;
                this.tipVideoReady = false;
                this.tipVideoLoadFailed = false;
                this.syncDerivedState();
                this.scheduleForceTipVideoLoad();
                this.scheduleRotationForActiveTip();
            });
    }

    private resolveTip(
        fallbackTip: ScreensaverTip,
        translatedTip: ScreensaverTipTranslation | undefined,
        manifest: LandingMediaManifest
    ): ScreensaverTip {
        const media = this.landingMedia.resolveSolutionMedia(fallbackTip.mediaKey, manifest);
        return {
            ...fallbackTip,
            title: translatedTip?.Title || fallbackTip.title,
            description: translatedTip?.Desc || fallbackTip.description,
            videoUrl: media.videoUrl,
            coverUrl: media.coverUrl
        };
    }

    private scheduleRotationForActiveTip(): void {
        const activeTip = this.activeTip;
        if (!activeTip) {
            this.clearTipRotationTimer();
            return;
        }

        if (activeTip.videoUrl) {
            this.scheduleTipRotation(this.tipVideoFallbackMs);
            return;
        }

        this.scheduleTipRotation(this.tipStaticFallbackMs);
    }

    private scheduleTipRotation(delayMs: number): void {
        this.clearTipRotationTimer();
        this.tipRotationTimeout = setTimeout(() => {
            this.advanceTip();
        }, delayMs);
    }

    private clearTipRotationTimer(): void {
        if (!this.tipRotationTimeout) {
            return;
        }

        clearTimeout(this.tipRotationTimeout);
        this.tipRotationTimeout = undefined;
    }

    private advanceTip(): void {
        if (this.tips.length <= 1) {
            this.scheduleForceTipVideoLoad();
            this.scheduleRotationForActiveTip();
            return;
        }

        this.activeTipIndex = (this.activeTipIndex + 1) % this.tips.length;
        this.tipVideoReady = false;
        this.tipVideoLoadFailed = false;
        this.syncDerivedState();
        this.scheduleForceTipVideoLoad();
        this.scheduleRotationForActiveTip();
    }

    private scheduleForceTipVideoLoad(): void {
        this.clearScheduledTipVideoLoad();
        this.scheduledTipVideoLoadRef = afterNextRender<TipVideoLoadContext, void>({
            earlyRead: () => this.resolveTipVideoLoadContext(),
            write: (context) => {
                this.scheduledTipVideoLoadRef = undefined;
                if (this.destroyRef.destroyed) {
                    return;
                }
                this.forceLoadActiveTipVideo(context);
            }
        }, { injector: this.injector });
    }

    private clearScheduledTipVideoLoad(): void {
        if (!this.scheduledTipVideoLoadRef) {
            return;
        }

        this.scheduledTipVideoLoadRef.destroy();
        this.scheduledTipVideoLoadRef = undefined;
    }

    private scheduleTipVideoStateUpdate(ready: boolean, failed: boolean, afterUpdate?: () => void): void {
        this.clearScheduledTipVideoStateUpdate();
        if (this.destroyRef.destroyed) {
            return;
        }

        this.scheduledTipVideoStateRef = afterNextRender(() => {
            this.scheduledTipVideoStateRef = undefined;
            if (this.destroyRef.destroyed) {
                return;
            }

            this.tipVideoReady = ready;
            this.tipVideoLoadFailed = failed;
            this.scheduleDerivedStateSync();
            afterUpdate?.();
        }, { injector: this.injector });
    }

    private clearScheduledTipVideoStateUpdate(): void {
        if (!this.scheduledTipVideoStateRef) {
            return;
        }

        this.scheduledTipVideoStateRef.destroy();
        this.scheduledTipVideoStateRef = undefined;
    }

    private resolveTipVideoLoadContext(): TipVideoLoadContext {
        const video = this.tipVideo?.nativeElement;
        const activeTip = this.activeTip;
        const expectedUrl = this.resolveAbsoluteAssetUrl(activeTip?.videoUrl);
        const currentUrl = video
            ? this.resolveAbsoluteAssetUrl(video.currentSrc || video.getAttribute('src'))
            : null;

        return { video, expectedUrl, currentUrl };
    }

    private forceLoadActiveTipVideo(context: TipVideoLoadContext): void {
        const { video, expectedUrl, currentUrl } = context;
        this.requestedTipVideoUrl = expectedUrl;
        if (!video || !expectedUrl) {
            return;
        }

        if (currentUrl !== expectedUrl) {
            video.pause();
            video.removeAttribute('src');
            video.src = expectedUrl;
        }

        video.defaultMuted = true;
        video.muted = true;
        video.volume = 0;
        video.currentTime = 0;
        video.load();
        video.play().catch(() => {
            if (!this.isCurrentTipVideoElement()) {
                return;
            }

            this.scheduleTipVideoStateUpdate(false, true, () => this.scheduleTipRotation(this.tipStaticFallbackMs));
        });
    }

    private isCurrentTipVideoEvent(event: Event): boolean {
        return this.isCurrentTipVideoElement(event.target as HTMLVideoElement | null);
    }

    private isCurrentTipVideoElement(video?: HTMLVideoElement | null): boolean {
        const target = video ?? this.tipVideo?.nativeElement ?? null;
        if (!target || !this.requestedTipVideoUrl) {
            return false;
        }

        const currentUrl = this.resolveAbsoluteAssetUrl(target.currentSrc || target.getAttribute('src'));
        return currentUrl === this.requestedTipVideoUrl;
    }

    private resolveAbsoluteAssetUrl(url: string | null | undefined): string | null {
        if (!url) {
            return null;
        }

        try {
            return new URL(url, window.location.origin).toString();
        } catch {
            return url;
        }
    }

    private showUnlock(): void {
        const wasVisible = this.unlockVisible;
        this.unlockVisible = true;
        if (this.overlayMode !== 'setup') {
            this.overlayMode = 'unlock';
        }
        this.hasPin = this.screenSaver.hasPin();
        this.pinErrorKey = null;
        this.showLogoutAction = false;
        if (this.isLockedOut()) {
            this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.LOCKED';
            this.showLogoutAction = true;
        }
        this.syncDerivedState();
        if (this.hasPin && !wasVisible) {
            this.pinControl.setValue('', { emitEvent: false });
            this.unlockInput?.clear();
        }
        if (this.overlayMode !== 'setup') {
            this.scheduleHidePanel();
        }
        if (this.hasPin && !this.isLockedOut()) {
            this.scheduleFocus('unlock');
        }
    }

    private scheduleShowUnlock(): void {
        this.clearScheduledUnlock();
        if (this.destroyRef.destroyed) {
            return;
        }

        this.scheduledUnlockRef = afterNextRender(() => {
            this.scheduledUnlockRef = undefined;
            if (this.destroyRef.destroyed) {
                return;
            }

            this.showUnlock();
        }, { injector: this.injector });
    }

    private clearScheduledUnlock(): void {
        if (!this.scheduledUnlockRef) {
            return;
        }

        this.scheduledUnlockRef.destroy();
        this.scheduledUnlockRef = undefined;
    }

    private resetSetupControls(): void {
        this.currentPinControl.setValue('');
        this.newPinControl.setValue('');
        this.pinErrorKey = null;
    }

    private resetPanelState(): void {
        this.overlayMode = 'unlock';
        this.hasPin = this.screenSaver.hasPin();
        this.setupStep = this.hasPin ? 'current' : 'new';
        this.pinControl.setValue('', { emitEvent: false });
        this.unlockInput?.clear();
        this.showLogoutAction = false;
        this.resetSetupControls();
        this.syncDerivedState();
    }

    private registerUnlockFailure(): void {
        this.failedUnlockAttempts += 1;
        this.pinControl.setValue('', { emitEvent: false });
        this.unlockInput?.clear();
        this.scheduleFocus('unlock');
        this.showLogoutAction = true;

        if (this.failedUnlockAttempts < 3) {
            this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.INVALID_CURRENT';
            return;
        }

        this.lockoutUntil = Date.now() + 60000;
        this.pinErrorKey = 'SECURITY.SCREEN_SAVER_PIN_ERRORS.LOCKED';
        this.updateLockoutRemaining();
        this.startLockoutTimer();
    }

    private isLockedOut(): boolean {
        return this.lockoutUntil > Date.now();
    }

    private startLockoutTimer(): void {
        this.clearLockoutTimer();
        this.lockoutTimer = setInterval(() => {
            this.updateLockoutRemaining();
            if (!this.isLockedOut()) {
                this.resetLockoutState();
            }
        }, 1000);
    }

    private updateLockoutRemaining(): void {
        const remainingMs = Math.max(0, this.lockoutUntil - Date.now());
        this.lockoutRemaining = Math.ceil(remainingMs / 1000);
    }

    private clearLockoutTimer(): void {
        if (!this.lockoutTimer) {
            return;
        }

        clearInterval(this.lockoutTimer);
        this.lockoutTimer = undefined;
    }

    logout(): void {
        this.screenSaver.deactivate({ navigate: false });
        this.authService.logout();
    }

    private resetLockoutState(): void {
        this.failedUnlockAttempts = 0;
        this.lockoutUntil = 0;
        this.lockoutRemaining = 0;
        this.showLogoutAction = false;
        this.clearLockoutTimer();
        if (this.pinErrorKey === 'SECURITY.SCREEN_SAVER_PIN_ERRORS.LOCKED') {
            this.pinErrorKey = null;
        }
        if (this.unlockVisible && this.hasPin) {
            this.scheduleFocus('unlock');
        }
        this.syncDerivedState();
    }

    private scheduleHidePanel(): void {
        this.clearHidePanelTimer();
        this.hidePanelTimeout = setTimeout(() => {
            this.unlockVisible = false;
            this.resetPanelState();
            this.syncDerivedState();
        }, this.panelIdleMs);
    }

    private clearHidePanelTimer(): void {
        if (!this.hidePanelTimeout) {
            return;
        }

        clearTimeout(this.hidePanelTimeout);
        this.hidePanelTimeout = undefined;
    }

    private scheduleFocus(target: 'unlock' | 'current' | 'new'): void {
        this.clearFocusTimer();
        this.focusTimeout = setTimeout(() => {
            if (target === 'unlock') {
                this.unlockInput?.focus();
                return;
            }
            if (target === 'current') {
                this.currentPinInput?.focus();
                return;
            }
            this.newPinInput?.focus();
        }, 50);
    }

    private clearFocusTimer(): void {
        if (!this.focusTimeout) {
            return;
        }
        clearTimeout(this.focusTimeout);
        this.focusTimeout = undefined;
    }

    private initializeOverlayMode(): void {
        const mode = this.screenSaver.getMode();
        if (mode === 'setup') {
            this.startSetup();
        }
    }

    private setUserIdentity(user: UserAccessToken | null): void {
        if (!user) {
            this.displayName = '';
            this.avatarUrl = null;
            this.avatarLoadFailed = false;
            this.syncDerivedState();
            return;
        }

        this.displayName = user.displayName || user.userName || 'User';
        this.avatarUrl = this.resolveAvatarUrl(user);
        this.avatarLoadFailed = false;
        this.syncDerivedState();
    }

    private resolveAvatarUrl(user: UserAccessToken): string | null {
        if (user.userId) {
            try {
                return this.imageService.getImageUrl(user.userId as any, 0.8, user.userId as any, 256);
            } catch (error) {
                // Fallback to legacy below
            }
        }

        if (user.avatar) {
            return this.dataService.getAvatarPhoto(user.avatar);
        }

        return null;
    }
}
